import { type ActionEvent, Controller } from "@hotwired/stimulus"
import { type TurboSubmitEndEvent } from "@hotwired/turbo"

import { errorShownEvent } from "@/helpers/ga4_helper"

const TRACKING_TIMEOUT = 10000

export default class LoaderController extends Controller<HTMLElement> {
  static targets = ["loader"]
  declare readonly loaderTarget: HTMLElement

  static values = {
    enableAutoStop: { default: true, type: Boolean }
  }
  declare readonly enableAutoStopValue: boolean

  private timeoutId?: number

  initialize(): void {
    this.stop = this.stop.bind(this)
  }

  connect(): void {
    if (this.enableAutoStopValue) {
      window.addEventListener("turbo:frame-render", this.stop)
      window.addEventListener("turbo:stream-render", this.stop)
    }
  }

  disconnect(): void {
    this.clearTimeout()

    if (this.enableAutoStopValue) {
      window.removeEventListener("turbo:frame-render", this.stop)
      window.removeEventListener("turbo:stream-render", this.stop)
    }
  }

  start(e?: ActionEvent): void {
    // Return if loader already started.
    if (this.timeoutId) {
      return
    }

    this.loaderTarget.hidden = false

    this.timeoutId = window.setTimeout(() => {
      // We don't actually show an error to the user, but we're requested to reuse this
      // error event to log overly-long spinners.
      errorShownEvent({
        eventGroup: "spinner_timeout",
        errorText: "Spinner timeout",
        errorId: (e?.target as HTMLElement)?.id || this.element.closest("[id]")?.id,
        payload: e?.params?.timeoutTrackingPayload ?? {}
      })

      this.timeoutId = undefined
    }, TRACKING_TIMEOUT)
  }

  stop(): void {
    this.loaderTarget.hidden = true
    this.clearTimeout()
  }

  async stopIfError({ detail: { fetchResponse } }: TurboSubmitEndEvent): Promise<void> {
    const hasError =
      !fetchResponse || (await fetchResponse.responseHTML).includes("turboErrorStream")

    if (hasError) {
      this.stop()
    }
  }

  private clearTimeout(): void {
    window.clearTimeout(this.timeoutId)
    this.timeoutId = undefined
  }
}
